import { EditorCardProvider } from '@atlaskit/editor-card-provider';
import { prefetchNodeDataProvidersData } from '@atlaskit/node-data-provider';
import { CardClient } from '@atlaskit/link-provider';

import { SSRMeasures } from '@confluence/action-measures';
import { createCookie } from '@confluence/content-smartlinks';
import { expVal } from '@confluence/feature-experiments';
import { getSSRRenderInputs } from '@confluence/ssr-utilities';

const MEASURE_SEGMENT = 'ssr-app/prepare/preloadQuery/editor/nodeDataProvidersData';

export async function preloadEditorNodeDataProvidersData(docJsonString: string | undefined) {
	if (!docJsonString) {
		return;
	}

	if (!isSSR()) {
		// If we are not in SSR, we don't need to preload node data providers data.
		return;
	}

	await SSRMeasures.run(MEASURE_SEGMENT, async () => {
		const doc = JSON.parse(docJsonString);

		const providers = [
			{
				provider: ssrEditorCardProvider(),
				// The same value as `DEFAULT_ORS_TIMEOUT` in next/packages/content-smartlinks/src/preloadContentSmartLinksConstants.ts
				timeout: expVal('platform_editor_smart_card_otp', 'timeout', 500),
				// Reasonable value for smart card prefetching.
				maxNodesToPrefetch: 10,
			},
		];

		providers.forEach(({ provider }) => {
			SSRMeasures.markMeasureStart(measureProviderSegment(provider.name));
		});

		const editorNodeDataProvidersData = await prefetchNodeDataProvidersData({
			doc,
			providers,
			timeout: expVal('platform_editor_smart_card_otp', 'timeout', 500),
			// Usually, one screen contains about 2_500 nodes, so 10_000 is more than enough.
			maxNodesToVisit: 10_000,
		});

		Object.entries(editorNodeDataProvidersData).forEach(([providerName, { duration }]) => {
			SSRMeasures.setMeasureDuration(measureProviderSegment(providerName), duration);
		});

		window['__SSR_EDITOR_NODE_DATA_PROVIDERS_DATA__'] = Object.fromEntries(
			Object.entries(editorNodeDataProvidersData).map(([providerName, { data }]) => [
				providerName,
				data,
			]),
		);
	});
}

function ssrEditorCardProvider() {
	const { host, cloudId, userContext, customURLs } = getSSRRenderInputs();

	// This URL will be something like `https://object-resolver.staging.atl-paas.net/resolve/batch`.
	// We need only protocol and host to pass it to the EditorCardProvider,
	// because path will be added by `CardClient`.
	const objectResolverURL =
		customURLs?.['object-resolver-service']?.match(/^(https?:\/\/[^/]+)/)?.[0];

	// We need to define a custom CardClient for SSR, because the default one works through public API gateway.
	const ssrCardClient = new CardClient('custom', objectResolverURL);
	// Headers from `fetchORSForSSR` from confluence/next/packages/content-smartlinks/src/index.ts
	ssrCardClient.setHeaders({
		Cookie: createCookie(userContext),
		Origin: host.startsWith('http') ? host : `https://${host}`,
		CloudId: cloudId,
	});

	return new EditorCardProvider(
		'custom',
		objectResolverURL,
		'CONFLUENCE',
		undefined,
		ssrCardClient,
	);
}

function isSSR(): boolean {
	try {
		return Boolean(
			// In most places there is no document when running on server-side
			typeof document === 'undefined' ||
				// When SSRing editor with full cycle mode we define the document
				// Check Confluence specific environment variable
				// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
				(typeof process !== 'undefined' && process.env.REACT_SSR),
		);
	} catch (e) {
		// Catch possible error that might occur and just return false
		return false;
	}
}

function measureProviderSegment(providerName: string) {
	return `${MEASURE_SEGMENT}/${providerName}`;
}
